import React, { useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '@/store/auth'

export interface SelectCustomerItem {
  id: number
  name: string
  email?: string
}

interface CustomerSelectModalProps {
  isOpen: boolean
  title?: string
  onClose: () => void
  onSelect: (item: SelectCustomerItem) => void
  loader: (query: string) => Promise<SelectCustomerItem[]>
}

export const CustomerSelectModal: React.FC<CustomerSelectModalProps> = ({ isOpen, title = 'Müşteri Seç', onClose, onSelect, loader }) => {
  const { user } = useAuthStore()
  const t = (tr: string, de: string) => (user?.role === 'staff' ? de : tr)
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<SelectCustomerItem[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let active = true
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await loader(query)
        if (active) setItems(res)
      } finally {
        if (active) setLoading(false)
      }
    }, 300)
    return () => { active = false; clearTimeout(timer) }
  }, [isOpen, query, loader])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl rounded-xl shadow-xl border border-gray-200">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">{user?.role === 'staff' ? 'Kunde auswählen' : title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <div className="p-4 space-y-4">
          <input
            value={query}
            onChange={(e)=>setQuery(e.target.value)}
            placeholder={t('Müşteri ara (ad, soyad, email)','Kunde suchen (Name, Nachname, E-Mail)')}
            className="w-full px-3 py-2 border rounded-md"
          />
          <div className="max-h-80 overflow-auto divide-y">
            {loading ? (
              <div className="p-4 text-gray-500">{t('Yükleniyor...','Lädt...')}</div>
            ) : items.length === 0 ? (
              <div className="p-4 text-gray-500">{t('Kayıt yok','Keine Daten')}</div>
            ) : (
              items.map(item => (
                <div key={item.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{item.name}</p>
                    {item.email && <p className="text-xs text-gray-500">{item.email}</p>}
                  </div>
                  <button onClick={()=>onSelect(item)} className="px-3 py-1 border rounded-md hover:bg-gray-100">{t('Seç','Auswählen')}</button>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-2 border rounded-md">{t('Kapat','Schließen')}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerSelectModal 